import React, { useEffect, useMemo, useState } from "react";
import { useMutationState } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Typography,
  useTheme,
  IconButton,
  Divider,
  Avatar,
  Chip,
  Fade,
  Snackbar,
  Stack,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { alpha } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import Category from "../../models/finance/Category";
import Expense from "../../models/finance/Expense";
import FormField from "../common/ui/FormField";
import PrimaryButton from "../common/ui/PrimaryButton";
import AmountInput from "../common/AmountInput";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useNavigate } from "react-router-dom";
import { CATEGORIES_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import { getCurrencySymbol } from "../../utils/currencies";
import { useUserProfileContext } from "../../contexts/ProfileContext";

interface ExpenseFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    id: string,
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => void;
  onSaveAndAdd: (
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string,
    onSuccess?: () => void
  ) => void,
  onDelete?: (expense: Expense) => void;
  mode: "add" | "edit";
  initialData?: Expense;
  selectedYear: number;
  selectedMonth: number;
  categories: Category[];
}

// Stable component reference keeps DatePicker section input behavior intact.
const PickerTextField: React.FC<any> = (props) => <FormField {...props} />;

const ExpenseFormDrawer: React.FC<ExpenseFormDrawerProps> = ({
  open,
  onClose,
  onSave,
  onSaveAndAdd,
  onDelete,
  mode,
  initialData,
  selectedYear,
  selectedMonth,
  categories,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { dateFormat } = useUserProfileContext();
  const navigate = useNavigate();
  const { currency } = useCurrencyFormatter();
  const currencySymbol = getCurrencySymbol(currency);
  const categoryOptions = useMemo(
    () => categories.map((cat) => cat.title).sort((a, b) => a.localeCompare(b)),
    [categories]
  );
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const { error, helpText, clearError } = useError();
  const [saveAndNextNotice, setSaveAndNextNotice] = useState("");
  const [amountInputKey, setAmountInputKey] = useState(0);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  
  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const setFieldError = (field: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };
  const clearAllFieldErrors = () => setFieldErrors({});
  const markFieldTouched = (field: string) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };
  const shouldShowFieldError = (field: string) => (hasAttemptedSubmit || touchedFields[field] ? fieldErrors[field] : undefined);

  const getDateError = (value: Dayjs | null) => (!value ? "Date is required" : "");
  const getAmountError = (value: string) => {
    if (!value || value.trim() === "") return "Amount is required";
    const numAmount = parseFloat(value);
    if (isNaN(numAmount) || numAmount <= 0) return "Amount must be greater than 0";
    return "";
  };
  const getDescriptionError = (value: string) => (!value || value.trim() === "" ? "Description is required" : "");
  const getCategoryError = (value: string) => (!value ? "Category is required" : "");

  // ✅ Observe expense mutation state globally (no prop drilling!)
  const isSaving = useMutationState({
    filters: { mutationKey: ['expenses'], status: 'pending' },
    select: (mutation) => mutation.state.status === 'pending',
  }).some(Boolean);

  const isFormValid = !getDateError(date) && !getAmountError(amount) && !getDescriptionError(description) && !getCategoryError(category);
  const isFormDirty = mode === "edit" && initialData
    ? (!date || !dayjs(initialData.date).isSame(date, "day"))
      || parseFloat(amount || "0") !== initialData.amount
      || description.trim() !== initialData.description.trim()
      || category !== initialData.category
    : Boolean(amount.trim() || description.trim() || category);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setDate(dayjs(initialData.date));
      setAmount(initialData.amount.toString());
      setDescription(initialData.description);
      setCategory(initialData.category);
    } else {
      // Default to today's date when adding a new expense
      setDate(dayjs());
      setAmount("");
      setDescription("");
      setCategory("");
    }
    clearError();
    clearAllFieldErrors();
    setTouchedFields({});
    setHasAttemptedSubmit(false);
    setSaveAndNextNotice("");
  }, [mode, initialData, selectedYear, selectedMonth, open, clearError]);

  useEffect(() => {
    if (!saveAndNextNotice) {
      return;
    }
    const timeoutId = setTimeout(() => setSaveAndNextNotice(""), 2200);
    return () => clearTimeout(timeoutId);
  }, [saveAndNextNotice]);

  const handleSave = () => {
    // Clear previous errors
    setHasAttemptedSubmit(true);
    clearError();
    clearAllFieldErrors();
    let hasErrors = fieldsValidation();
    
    if (hasErrors) {
      return;
    }

    const numAmount = parseFloat(amount);
    const expenseId = initialData?.id || "";
    onSave(expenseId, date!.toDate(), numAmount, description, category);
  };

  const handleSaveAndNew = () => {
    // Clear previous errors
    setHasAttemptedSubmit(true);
    clearError();
    clearAllFieldErrors();
    let hasErrors = fieldsValidation();
    
    if (hasErrors) {
      return;
    }

    const numAmount = parseFloat(amount);
    onSaveAndAdd(date!.toDate(), numAmount, description, category, () => {
      setAmount("");
      setDescription("");
      setTouchedFields({});
      setHasAttemptedSubmit(false);
      setSaveAndNextNotice("Saved. Ready for your next expense.");
      setAmountInputKey((prev) => prev + 1);
    });
  };

  const fieldsValidation = () => {
    // Field-level validation
    let hasErrors = false;
    const dateError = getDateError(date);
    const amountError = getAmountError(amount);
    const descriptionError = getDescriptionError(description);
    const categoryError = getCategoryError(category);

    if (dateError) {
      setFieldError("date", dateError);
      hasErrors = true;
    } else {
      clearFieldError("date");
    }

    if (amountError) {
      setFieldError("amount", amountError);
      hasErrors = true;
    } else {
      clearFieldError("amount");
    }

    if (descriptionError) {
      setFieldError("description", descriptionError);
      hasErrors = true;
    } else {
      clearFieldError("description");
    }

    if (categoryError) {
      setFieldError("category", categoryError);
      hasErrors = true;
    } else {
      clearFieldError("category");
    }
    
    return hasErrors;

  } 

  const handleDelete = () => {
    if (initialData && onDelete) {
      onDelete(initialData);
    }
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 3, // Higher than header
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100%",
          height: { xs: "92dvh", sm: "100%" },
          background: theme.palette.background.default,
          borderLeft: { sm: "1px solid rgba(255,255,255,0.08)" },
          borderTop: { xs: "1px solid rgba(255,255,255,0.08)", sm: "none" },
          borderTopLeftRadius: { xs: 18, sm: 0 },
          borderTopRightRadius: { xs: 18, sm: 0 },
          overflow: "hidden",
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 2,
              background: `linear-gradient(160deg, ${alpha(theme.palette.error.main, 0.22)}, ${alpha(theme.palette.background.paper, 0.4)})`,
              color: theme.palette.text.primary,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -16,
                right: -18,
                width: 140,
                height: 140,
                background: alpha(theme.palette.error.main, 0.15),
                borderRadius: "50%",
                filter: "blur(2px)",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      backgroundColor: alpha(theme.palette.error.main, 0.2),
                      color: theme.palette.error.light,
                    }}
                  >
                    <ReceiptIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {mode === "add" ? "Add Expense" : "Edit Expense"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {mode === "add" ? "Log a transaction with clean details" : "Adjust this transaction safely"}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={onClose}
                  sx={{
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.common.white, 0.06),
                    "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.12) },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              
              {mode === "edit" && (
                <Chip
                  label="Editing existing entry"
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                    color: theme.palette.error.light,
                    fontWeight: 600,
                  }}
                />
              )}

            </Box>
          </Box>

          <Snackbar
            open={Boolean(saveAndNextNotice)}
            autoHideDuration={2200}
            onClose={() => setSaveAndNextNotice("")}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{ top: { xs: 76, sm: 84 } }}
          >
            <Alert
              severity="success"
              variant="filled"
              onClose={() => setSaveAndNextNotice("")}
              sx={{
                borderRadius: 1.5,
                bgcolor: theme.palette.success.dark,
                color: theme.palette.success.contrastText,
                boxShadow: "0 10px 24px rgba(2,6,23,0.45)",
              }}
            >
              {saveAndNextNotice}
            </Alert>
          </Snackbar>

          {/* Form Content */}
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflowY: "auto" }}
          >
            <ErrorAlert
              error={error}
              helpText={helpText}
              onClear={clearError}
              onRetry={handleSave}
              title="Expense Form Error"
              showRetry={false}
            />

            <Stack spacing={2.25}>
              {/* Date Picker */}
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: theme.palette.background.paper, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={date}
                    format={dateFormat}
                    onChange={(newDate) => {
                      setDate(newDate);
                      if (hasAttemptedSubmit || touchedFields.date) {
                        const msg = getDateError(newDate);
                        if (msg) setFieldError("date", msg);
                        else clearFieldError("date");
                      }
                    }}
                    slots={{ textField: PickerTextField }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onBlur: () => {
                          markFieldTouched("date");
                          const msg = getDateError(date);
                          if (msg) setFieldError("date", msg);
                          else clearFieldError("date");
                        },
                        error: !!shouldShowFieldError("date"),
                        helperText: shouldShowFieldError("date"),
                        InputProps: {
                          startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setDate(dayjs())}
                    sx={{ minWidth: "auto", px: 1.25, color: theme.palette.text.secondary }}
                  >
                    Today
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setDate(dayjs().subtract(1, "day"))}
                    sx={{ minWidth: "auto", px: 1.25, color: theme.palette.text.secondary }}
                  >
                    Yesterday
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setDate(dayjs().startOf("month"))}
                    sx={{ minWidth: "auto", px: 1.25, color: theme.palette.text.secondary }}
                  >
                    Month start
                  </Button>
                </Stack>
              </Box>

              {/* Amount */}
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: theme.palette.background.paper, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Amount
                </Typography>
                <AmountInput
                  key={amountInputKey}
                  fullWidth
                  value={amount}
                  onChange={(value) => {
                    setAmount(value);
                    if (hasAttemptedSubmit || touchedFields.amount) {
                      const msg = getAmountError(value);
                      if (msg) setFieldError("amount", msg);
                      else clearFieldError("amount");
                    }
                  }}
                  onBlur={() => {
                    markFieldTouched("amount");
                    if (!amount || amount.trim() === "") {
                      clearFieldError("amount");
                      return;
                    }
                    const msg = getAmountError(amount);
                    if (msg) setFieldError("amount", msg);
                    else clearFieldError("amount");
                  }}
                  autoFocus={mode === "add"}
                  placeholder="0.00"
                  maxDecimals={2}
                  error={!!shouldShowFieldError("amount")}
                  helperText={shouldShowFieldError("amount") || "Use positive values only"}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ mr: 1, color: 'action.active', fontWeight: 600 }}>
                        {currencySymbol}
                      </Typography>
                    ),
                  }}
                />
              </Box>

              {/* Description */}
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: theme.palette.background.paper, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Description
                </Typography>
                <FormField
                  fullWidth
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (hasAttemptedSubmit || touchedFields.description) {
                      const msg = getDescriptionError(e.target.value);
                      if (msg) setFieldError("description", msg);
                      else clearFieldError("description");
                    }
                  }}
                  onBlur={() => {
                    markFieldTouched("description");
                    const msg = getDescriptionError(description);
                    if (msg) setFieldError("description", msg);
                    else clearFieldError("description");
                  }}
                  placeholder="What did you spend on?"
                  error={!!shouldShowFieldError("description")}
                  helperText={shouldShowFieldError("description") || `${description.length}/50 characters`}
                  inputProps={{ maxLength: 50 }}
                  InputProps={{
                    startAdornment: <DescriptionIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Box>

              {/* Category */}
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: theme.palette.background.paper, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                  Category
                </Typography>
                <Autocomplete
                  options={categoryOptions}
                  value={category || null}
                  onChange={(_, newValue) => {
                    const value = newValue || "";
                    setCategory(value);
                    if (hasAttemptedSubmit || touchedFields.category) {
                      const msg = getCategoryError(value);
                      if (msg) setFieldError("category", msg);
                      else clearFieldError("category");
                    }
                  }}
                  onBlur={() => {
                    markFieldTouched("category");
                    const msg = getCategoryError(category);
                    if (msg) setFieldError("category", msg);
                    else clearFieldError("category");
                  }}
                  noOptionsText={
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.secondary }}>
                        No matching categories
                      </Typography>
                      <Button size="small" onClick={() => navigate(CATEGORIES_ROUTE_PATH)}>
                        Create category
                      </Button>
                    </Box>
                  }
                  renderInput={(params) => (
                    <FormField
                      {...params}
                      placeholder="Search categories..."
                      error={!!shouldShowFieldError("category")}
                      helperText={shouldShowFieldError("category") || "Type to filter categories"}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <CategoryIcon sx={{ mr: 1, color: 'action.active' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate(CATEGORIES_ROUTE_PATH)}
                  sx={{ mt: 1, px: 0.5, color: theme.palette.primary.light }}
                >
                  Manage categories
                </Button>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Footer Actions */}
          <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: theme.palette.background.paper, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between">
              {mode === "edit" && onDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  sx={{
                    borderRadius: 1.5,
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    width: { xs: "100%", sm: "auto" },
                    '&:hover': {
                      backgroundColor: theme.palette.error.light + '20',
                      borderColor: theme.palette.error.dark,
                    },
                  }}
                >
                  Delete
                </Button>
              )}
              
              <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={isSaving}
                  sx={{
                    borderRadius: 1.5,
                    minWidth: 100,
                    borderColor: 'rgba(148,163,184,0.45)',
                    color: theme.palette.text.secondary,
                    flex: { xs: 1, sm: "unset" },
                    '&:hover': {
                      borderColor: 'rgba(148,163,184,0.7)',
                      backgroundColor: alpha(theme.palette.common.white, 0.04),
                    },
                  }}
                >
                  Cancel
                </Button>
                
                <PrimaryButton
                  type="button"
                  startIcon={isSaving ? null : <SaveIcon />}
                  onClick={handleSave}
                  disabled={isSaving || !isFormValid || !isFormDirty}
                  sx={{ minWidth: 120, flex: { xs: 1, sm: "unset" } }}
                >
                  {isSaving ? <CircularProgress size={18} color="inherit" /> : mode === "add" ? "Save" : "Update"}
                </PrimaryButton>

                {mode === "add" && (
                  <PrimaryButton
                    type="button"
                    startIcon={isSaving ? null : <SaveIcon />}
                    onClick={handleSaveAndNew}
                    disabled={isSaving || !isFormValid}
                    sx={{ minWidth: 120, flex: { xs: 1, sm: "unset" }, background: "transparent", border: `1px solid ${alpha(theme.palette.primary.main, 0.55)}`, color: theme.palette.primary.light, boxShadow: "none", '&:hover': { background: alpha(theme.palette.primary.main, 0.12), boxShadow: "none" } }}
                  >
                    {isSaving ? <CircularProgress size={18} color="inherit" /> : "Save & New"}
                  </PrimaryButton>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Drawer>
  );
};

export default ExpenseFormDrawer;
